/**
 * @fileoverview Cache management for table data
 *
 * Provides in-memory caching with TTL (time-to-live) for table data
 * to reduce redundant AJAX requests and improve performance.
 *
 * @module CacheManager
 */

import { DEFAULTS } from './constants.js';

/**
 * @typedef {import('./types.js').CacheEntry} CacheEntry
 */

/**
 * CacheManager handles data caching with TTL
 * @class
 */
export class CacheManager {
  /**
   * Create a CacheManager instance
   * @param {Object} options - Configuration options
   * @param {boolean} [options.enabled=true] - Enable caching
   * @param {number} [options.expiry=300000] - Cache expiry in milliseconds (default: 5 min)
   * @param {number} [options.maxEntries=100] - Maximum cache entries (prevents memory bloat)
   */
  constructor(options = {}) {
    this.enabled = options.enabled !== false;
    this.expiry = options.expiry || DEFAULTS.CACHE_EXPIRY;
    this.maxEntries = options.maxEntries || 100;

    // Cache storage: Map<key, CacheEntry>
    this.cache = new Map();

    // Metrics
    this.metrics = {
      hits: 0,
      misses: 0,
      evictions: 0,
      sets: 0
    };
  }

  /**
   * Get data from cache
   * @param {string} key - Cache key
   * @returns {Array|null} Cached data or null if not found/expired
   */
  get(key) {
    if (!this.enabled) {
      return null;
    }

    const entry = this.cache.get(key);

    if (!entry) {
      this.metrics.misses++;
      return null;
    }

    // Check if expired
    const age = Date.now() - entry.timestamp;
    if (age >= this.expiry) {
      this.cache.delete(key);
      this.metrics.misses++;
      return null;
    }

    this.metrics.hits++;
    return entry.data;
  }

  /**
   * Store data in cache
   * @param {string} key - Cache key
   * @param {Array} data - Data to cache
   * @returns {boolean} True if stored successfully
   */
  set(key, data) {
    if (!this.enabled) {
      return false;
    }

    // Enforce max entries (LRU eviction)
    if (this.cache.size >= this.maxEntries && !this.cache.has(key)) {
      this._evictOldest();
    }

    const entry = {
      data: data,
      timestamp: Date.now(),
      key: key,
      size: this._estimateSize(data)
    };

    this.cache.set(key, entry);
    this.metrics.sets++;

    return true;
  }

  /**
   * Check if cache has valid entry for key
   * @param {string} key - Cache key
   * @returns {boolean} True if valid cached data exists
   */
  has(key) {
    if (!this.enabled) {
      return false;
    }

    const data = this.get(key);
    return data !== null;
  }

  /**
   * Invalidate (remove) cache entry
   * @param {string} key - Cache key
   * @returns {boolean} True if entry was removed
   */
  invalidate(key) {
    return this.cache.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clear() {
    this.cache.clear();
    this.metrics = {
      hits: 0,
      misses: 0,
      evictions: 0,
      sets: 0
    };
  }

  /**
   * Get cache metrics
   * @returns {Object} Cache statistics
   */
  getMetrics() {
    const hitRate = this.metrics.hits + this.metrics.misses > 0
      ? (this.metrics.hits / (this.metrics.hits + this.metrics.misses)) * 100
      : 0;

    return {
      ...this.metrics,
      hitRate: hitRate.toFixed(2),
      entries: this.cache.size,
      enabled: this.enabled,
      totalSize: this._getTotalSize()
    };
  }

  /**
   * Get all cache keys
   * @returns {Array<string>} Array of cache keys
   */
  getKeys() {
    return Array.from(this.cache.keys());
  }

  /**
   * Get cache entry info
   * @param {string} key - Cache key
   * @returns {Object|null} Entry metadata or null
   */
  getEntryInfo(key) {
    const entry = this.cache.get(key);
    if (!entry) {
      return null;
    }

    const age = Date.now() - entry.timestamp;
    return {
      key: entry.key,
      timestamp: entry.timestamp,
      age: age,
      expired: age >= this.expiry,
      size: entry.size,
      rowCount: Array.isArray(entry.data) ? entry.data.length : 0
    };
  }

  /**
   * Clean up expired entries
   * @returns {number} Number of entries removed
   */
  cleanup() {
    let removed = 0;
    const now = Date.now();

    for (const [key, entry] of this.cache.entries()) {
      const age = now - entry.timestamp;
      if (age >= this.expiry) {
        this.cache.delete(key);
        removed++;
      }
    }

    if (removed > 0) {
      console.log(`CacheManager: Cleaned up ${removed} expired entries`);
    }

    return removed;
  }

  /**
   * Evict oldest entry (LRU)
   * @private
   */
  _evictOldest() {
    // Find oldest entry
    let oldestKey = null;
    let oldestTime = Infinity;

    for (const [key, entry] of this.cache.entries()) {
      if (entry.timestamp < oldestTime) {
        oldestTime = entry.timestamp;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
      this.metrics.evictions++;
      console.log(`CacheManager: Evicted oldest entry (${oldestKey})`);
    }
  }

  /**
   * Estimate size of data in bytes
   * @private
   * @param {*} data - Data to measure
   * @returns {number} Estimated size in bytes
   */
  _estimateSize(data) {
    try {
      // Rough estimate: JSON string length
      return JSON.stringify(data).length;
    } catch (error) {
      // Fallback: assume moderate size
      return 1024; // 1KB
    }
  }

  /**
   * Get total cache size estimate
   * @private
   * @returns {number} Total size in bytes
   */
  _getTotalSize() {
    let total = 0;
    for (const entry of this.cache.values()) {
      total += entry.size || 0;
    }
    return total;
  }

  /**
   * Enable caching
   */
  enable() {
    this.enabled = true;
    console.log('CacheManager: Caching enabled');
  }

  /**
   * Disable caching
   * @param {boolean} [clearExisting=false] - Clear existing cache
   */
  disable(clearExisting = false) {
    this.enabled = false;
    if (clearExisting) {
      this.clear();
    }
    console.log('CacheManager: Caching disabled');
  }

  /**
   * Set cache expiry time
   * @param {number} expiry - Expiry in milliseconds
   */
  setExpiry(expiry) {
    if (typeof expiry !== 'number' || expiry < 0) {
      throw new Error('CacheManager: Expiry must be a positive number');
    }
    this.expiry = expiry;
    console.log(`CacheManager: Expiry set to ${expiry}ms`);
  }

  /**
   * Get cache configuration
   * @returns {Object} Configuration
   */
  getConfig() {
    return {
      enabled: this.enabled,
      expiry: this.expiry,
      maxEntries: this.maxEntries
    };
  }

  /**
   * Generate cache key from URL and parameters
   * @param {string|Object} ajax - AJAX config or URL
   * @param {Object} [params] - Additional parameters
   * @returns {string} Cache key
   */
  static generateKey(ajax, params = {}) {
    let url;
    let method = 'GET';
    let data = {};

    if (typeof ajax === 'string') {
      url = ajax;
    } else if (typeof ajax === 'object') {
      url = ajax.url || '';
      method = ajax.type || ajax.method || 'GET';
      data = ajax.data || {};
    } else {
      url = String(ajax);
    }

    // Combine URL, method, data, and params into a key
    const keyObj = {
      url,
      method: method.toUpperCase(),
      data,
      params
    };

    // Create deterministic key
    return JSON.stringify(keyObj);
  }

  /**
   * Create a cache key from URL only (simpler version)
   * @param {string} url - URL
   * @returns {string} Cache key
   */
  static generateSimpleKey(url) {
    return `url:${url}`;
  }
}

export default CacheManager;
